/**
 * AppShell — the frame every authenticated page lives inside.
 *
 * Spec: Docs/design/04-shell-and-navigation.md §2
 *
 * Topbar (44px) + Sidebar (240/56/0px) + ContentArea (fluid).
 * Sidebar state persisted via localStorage.
 * Global Cmd+K opens CommandPalette.
 * OnboardingModal shown once on first login.
 */
import * as React from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { DesktopTitleBar } from "./DesktopTitleBar";
import { CommandPalette } from "./CommandPalette";
import { OnboardingModal } from "./OnboardingModal";
import { ONBOARDING_DONE_KEY } from "./onboarding";
import { PaneShell } from "@/modules/pane/PaneShell";
import { VoiceCallProvider } from "@/components/voice/VoiceCallContext";
import { FloatingVoiceOrb } from "@/components/voice/FloatingVoiceOrb";
import { SessionPanelProvider } from "@/contexts/SessionPanelContext";
import { SessionPanelSlot } from "@/components/chat/SessionPanelSlot";
import { DeviceActivityDock } from "@/components/devices/DeviceActivityDock";

const SIDEBAR_KEY = "omni:sidebar-collapsed";

export function AppShell() {
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "true"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [showOnboarding, setShowOnboarding] = React.useState(() => {
    try { return !localStorage.getItem(ONBOARDING_DONE_KEY); } catch { return false; }
  });

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((v) => {
      try { localStorage.setItem(SIDEBAR_KEY, String(!v)); } catch { /* noop */ }
      return !v;
    });
  }, []);

  // Keyboard shortcut: Cmd+B / Ctrl+B → toggle sidebar
  // Keyboard shortcut: Cmd+K / Ctrl+K → open command palette
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        toggleCollapsed();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleCollapsed]);

  return (
    <VoiceCallProvider>
      <SessionPanelProvider>
      <div className="flex h-screen flex-col bg-bg text-fg overflow-hidden">
        {/* Frameless titlebar — Electron desktop only; null on web/mobile. */}
        <DesktopTitleBar />
        {/* Live view of what the agent is doing to this machine's files.
            Desktop-only and self-hiding when idle. */}
        <DeviceActivityDock />
        <div className="flex flex-1 min-h-0">
        {/* Sidebar + optional session panel (Home chats) */}
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleCollapsed}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <SessionPanelSlot sidebarCollapsed={collapsed} />

        {/* Main column */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Topbar */}
          <Topbar
            onMenuClick={() => setMobileOpen(true)}
            sidebarCollapsed={collapsed}
            onSearchClick={() => setPaletteOpen(true)}
          />

          {/* Content area — Home fills height for chat layout */}
          <main className="flex flex-1 flex-col min-h-0 overflow-hidden">
            <div className="mx-auto flex h-full w-full max-w-[1440px] flex-col min-h-0">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Omni Pane — right-side d&d canvas mirroring the left sidebar.
            Toggle with Cmd/Ctrl+. ; fullscreen with Cmd/Ctrl+Shift+. .
            See dashboard/src/modules/pane/PaneShell.tsx and
            Docs/architecture/24-omni-pane.md. */}
        <PaneShell />
        </div>

        {/* Command palette overlay */}
        <CommandPalette
          isOpen={paletteOpen}
          onClose={() => setPaletteOpen(false)}
        />

        {/* First-run onboarding */}
        {showOnboarding && (
          <OnboardingModal onDone={() => setShowOnboarding(false)} />
        )}

        {/* Global Floating Voice Orb assistant */}
        <FloatingVoiceOrb />
      </div>
      </SessionPanelProvider>
    </VoiceCallProvider>
  );
}
